import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { isLessThanBreakpoint } from 'dpl/util/grid';
import { singleBreederProfileResourceDefinition } from 'dpl/shared/utils/tptConnectResourceDefinitions';
import { resetParams } from 'dpl/actions/query_params';
import { ADOPTER_APPLICATION_OPEN } from 'dpl/constants/query_params';
import useResourceQuery from 'dpl/shared/fetching/hooks/useResourceQuery';
import useCurrentUser from 'dpl/common/hooks/useCurrentUser';
import SmartImage from 'dpl/components/SmartImage';
import Icon from 'dpl/common/components/Icon';
import MiniOverlayCloseButton from 'dpl/common/mini_overlay_modal/components/MiniOverlayModalCloseButton';
import BuyerApplicationAuthModal from 'dpl/buyer_account/components/BuyerApplicationAuthModal';
import LoadingWrapper from 'dpl/components/LoadingWrapper';

import signupHands from 'dpl/assets/application_hands.svg';

const IMAGE_DIMS = isLessThanBreakpoint('md')
  ? { width: '88px', height: '88px' }
  : { width: '140px', height: '140px' };

export default function BuyerApplicationIntroInterstice() {
  const [didStartAuthFlow, setDidStartAuthFlow] = useState(false);
  const breederProfileId = useSelector(({ server }) => server.breederProfileId);
  const cityState = useSelector(({ server }) => server.cityState);
  const dispatch = useDispatch();

  const {
    value: currentUser,
    isLoggedIn,
    isFetching: currentUserIsFetching,
    fetch
  } = useCurrentUser();

  const { value: breederProfile, isFetching: breederProfileIsFetching } =
    useResourceQuery(singleBreederProfileResourceDefinition(breederProfileId));

  const breederName = breederProfile?.name;

  function handleNextClick() {
    if (!isLoggedIn) {
      setDidStartAuthFlow(true);
    }
  }

  function handleCloseClick() {
    dispatch(resetParams(ADOPTER_APPLICATION_OPEN));
  }

  return (
    <LoadingWrapper
      isLoading={currentUserIsFetching || breederProfileIsFetching}
    >
      {() => (
        <div className="BuyerApplicationIntroInterstice vh-100 bg-white tc">
          {didStartAuthFlow && (
            <BuyerApplicationAuthModal
              onCloseClick={handleCloseClick}
              onSuccessfulAuth={fetch}
            />
          )}
          <div className="mb4 mb16-lg mw-100">
            <SmartImage
              Tag="div"
              forceOriginalSrc
              src={signupHands}
              width="760px"
              height="180px"
              className="BuyerApplicationIntroInterstice__hands mw-100 center bg-top no-repeat contain"
            />
            <MiniOverlayCloseButton onCloseClick={() => {}} />
          </div>
          <div className="BuyerApplicationIntroInterstice__content center ph4 pb20">
            <p className="f4 fw-medium f6-md mb6">Apply for a puppy from</p>
            <div className="mb2 mb4-lg">
              <SmartImage
                src={breederProfile.profile_photo_url}
                className="br-100"
                loadingClass="bg-light-gray"
                crop
                {...IMAGE_DIMS}
              />
            </div>
            <p className="fw-medium f4">{breederName}</p>
            <div className="mb8">
              {cityState && <p className="mb1">{cityState}</p>}
            </div>
            <p className="mb8">
              {currentUser?.data?.buyer_profile_id ? (
                <>
                  Welcome back, {currentUser.data.first_name}! Looks like you
                  already filled out an application so some of your answers will
                  be prefilled from your profile. {breederName} has a few
                  additional questions to make sure they find you the right pup.
                  It should only take a few minutes!
                </>
              ) : (
                <>
                  We&apos;d like to introduce you to {breederName} by asking you
                  a few questions. Your answers will help them better understand
                  your background, preferences, and ultimately find you the
                  right pup.
                </>
              )}
            </p>
            <button
              type="button"
              onClick={handleNextClick}
              className="button button--primary button--large pv3 inline-flex fw-medium w-100 w-auto-ns align-items-center"
            >
              <span className="flex-auto">
                {currentUser?.data?.buyer_profile_id
                  ? 'Continue application'
                  : 'Begin application'}
              </span>
              <Icon className="ml2-md" name="lined-arrow-right" />
            </button>
          </div>
        </div>
      )}
    </LoadingWrapper>
  );
}
